import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, PhoneCall, UserRound, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { org } from "../mock";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    toast({ title: "Message sent! ✉️", description: "Thanks for reaching out—we'll reply within one business day." });
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  const info = [
    { icon: MapPin, label: "Mailing Address", value: `Dallas Early Childhood Resource Institute, ${org.address}` },
    { icon: Phone, label: "Main Contact Line", value: org.phone },
    { icon: PhoneCall, label: "Alternative Business Line", value: org.altPhone },
    { icon: Mail, label: "Email", value: org.email },
    { icon: UserRound, label: "Director", value: org.director },
    { icon: Globe, label: "Website", value: org.website },
    { icon: Clock, label: "Hours", value: org.hours },
  ];

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative text-center max-w-3xl mx-auto">
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Contact</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">We’d love to hear from you</h1>
          <p className="mt-5 text-lg text-muted-foreground">Questions about programs, volunteering, or donations? Reach out anytime.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-prc grid lg:grid-cols-5 gap-10 items-start">
          <Reveal className="lg:col-span-2 space-y-4">
            {info.map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.label} className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-soft">
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-primary/10 text-primary shrink-0"><Icon className="h-6 w-6" /></span>
                  <div>
                    <p className="font-display font-bold text-foreground">{it.label}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{it.value}</p>
                  </div>
                </div>
              );
            })}
          </Reveal>

          <Reveal delay={120} className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white rounded-[1.75rem] p-7 lg:p-9 shadow-lift space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Name</Label>
                  <Input required value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-xl" placeholder="Your name" />
                </div>
                <div>
                  <Label className="font-semibold">Email</Label>
                  <Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-xl" placeholder="you@email.com" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Subject</Label>
                <Input value={form.subject} onChange={set("subject")} className="mt-1.5 h-11 rounded-xl" placeholder="How can we help?" />
              </div>
              <div>
                <Label className="font-semibold">Message</Label>
                <Textarea required value={form.message} onChange={set("message")} className="mt-1.5 rounded-xl min-h-[140px]" placeholder="Write your message..." />
              </div>
              <Button type="submit" className="btn-cta w-full bg-primary hover:bg-primary/90 rounded-full font-bold h-12"><Send className="h-5 w-5 mr-2" /> Send Message</Button>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-prc">
          <Reveal className="rounded-[2rem] overflow-hidden shadow-lift h-80">
            <iframe
              title="map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-97.02%2C33.02%2C-96.96%2C33.07&layer=mapnik&marker=33.046%2C-96.994"
              className="w-full h-full border-0"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
